import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, Eye, Trash2, Plus, Library } from "lucide-react";
import { AdminLayout } from "./AdminLayout";
import { adminCreateKnowledge, adminGetKnowledge, adminUpdateKnowledge } from "@/services/knowledgeService";
import { KNOWLEDGE_CATEGORIES } from "@/constants/knowledgeCategories";

/**
 * Admin · Knowledge Editor — create / edit a single story.
 *
 * Simple form (no rich text editor per user's directive "keep the admin
 * workflow simple"). Structured fields cover ماذا حدث / لماذا نجح / ماذا
 * نتعلم. Dynamic list for sources. Preview button opens the public
 * detail URL in a new tab.
 */

const EMPTY = {
  slug: "",
  category: "person",
  subjectName: "",
  title: "",
  intro: "",
  whatHappened: "",
  whySucceeded: "",
  lessonsLearned: "",
  readTimeMinutes: 2,
  country: "",
  industry: "",
  imageUrl: "",
  imageAlt: "",
  imageSourceName: "",
  imageSourceUrl: "",
  imageLicenseOrUsageNote: "",
  sources: [],
  status: "draft",
  isFeatured: false,
  displayOrder: 9999,
  publishedDisplay: "",
};

export default function AdminKnowledgeEditorPage() {
  const { id } = useParams();
  const isNew  = !id || id === "new";
  const navigate = useNavigate();

  const [form, setForm]     = useState(EMPTY);
  const [loading, setLoad]  = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [missingFields, setMissingFields] = useState(new Set());

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const row = await adminGetKnowledge(id);
        setForm({
          slug:              row.slug,
          category:          row.category,
          subjectName:       row.subjectName,
          title:             row.title,
          intro:             row.intro,
          whatHappened:      row.whatHappened,
          whySucceeded:      row.whySucceeded,
          lessonsLearned:    row.lessonsLearned,
          readTimeMinutes:   row.readTimeMinutes || 2,
          country:           row.country || "",
          industry:          row.industry || "",
          imageUrl:                row.imageUrl || "",
          imageAlt:                row.imageAlt || "",
          imageSourceName:         row.imageSourceName || "",
          imageSourceUrl:          row.imageSourceUrl || "",
          imageLicenseOrUsageNote: row.imageLicenseOrUsageNote || "",
          sources:                 Array.isArray(row.sources) ? row.sources : [],
          status:            row.status,
          isFeatured:        !!row.isFeatured,
          displayOrder:      row.displayOrder ?? 9999,
          publishedDisplay:  row.publishedDisplay || "",
        });
      } catch (e) {
        toast.error("تعذّر تحميل القصّة", { description: String(e.message || e) });
      } finally {
        setLoad(false);
      }
    })();
  }, [id, isNew]);

  const wordCount = useMemo(() => {
    const merged = [form.intro, form.whatHappened, form.whySucceeded, form.lessonsLearned].join(" ");
    return merged.trim().split(/\s+/).filter(Boolean).length;
  }, [form.intro, form.whatHappened, form.whySucceeded, form.lessonsLearned]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const addSource = () =>
    update({ sources: [...form.sources, { name: "", url: "" }] });
  const updSource = (i, patch) => {
    const next = form.sources.slice();
    next[i] = { ...next[i], ...patch };
    update({ sources: next });
  };
  const rmSource = (i) => {
    const next = form.sources.slice();
    next.splice(i, 1);
    update({ sources: next });
  };

  const REQUIRED_LABELS = {
    slug:           "المعرف (Slug)",
    subjectName:    "الاسم (شخصية أو شركة)",
    title:          "العنوان",
    intro:          "المقدّمة",
    whatHappened:   "ما الذي حدث؟",
    whySucceeded:   "لماذا نجح؟",
    lessonsLearned: "ماذا نتعلّم؟",
  };

  const validate = () => {
    const missing = Object.keys(REQUIRED_LABELS).filter(
      (k) => !String(form[k] || "").trim()
    );
    return missing;
  };

  const save = async (nextStatus) => {
    // Publishing needs full content; drafts can be saved incomplete.
    if (nextStatus === "published" || !isNew) {
      const missing = validate();
      if (missing.length > 0) {
        toast.error("لا يمكن الحفظ — حقول مطلوبة ناقصة", {
          description: missing.map((k) => `• ${REQUIRED_LABELS[k]}`).join("\n"),
        });
        setMissingFields(new Set(missing));
        return;
      }
    }
    setMissingFields(new Set());
    setSaving(true);
    try {
      const payload = { ...form };
      if (nextStatus) payload.status = nextStatus;
      // Send only cleaned sources.
      payload.sources = (form.sources || []).filter((s) => (s.name || s.url));

      if (isNew) {
        const r = await adminCreateKnowledge(payload);
        toast.success("أُنشئت القصّة");
        navigate(`/admin/knowledge/${r.slug}`, { replace: true });
      } else {
        await adminUpdateKnowledge(id, payload);
        toast.success(nextStatus === "published" ? "نُشرت القصّة" : "حُفظت التغييرات");
      }
    } catch (e) {
      const raw = String(e.message || e);
      // Backend format: "POST /admin/knowledge failed: 400 ({\"detail\":\"missing required fields: [...]\"})"
      const m = raw.match(/missing required fields:\s*\[([^\]]+)\]/i);
      if (m) {
        const keys = m[1].split(",").map((s) => s.trim().replace(/['"]/g, ""));
        toast.error("لا يمكن الحفظ — حقول مطلوبة ناقصة", {
          description: keys.map((k) => `• ${REQUIRED_LABELS[k] || k}`).join("\n"),
        });
        setMissingFields(new Set(keys));
      } else {
        toast.error("تعذّر الحفظ", { description: raw });
      }
    } finally {
      setSaving(false);
    }
  };

  const preview = () => {
    if (!form.slug) {
      toast.error("لا يمكن المعاينة قبل حفظ القصّة");
      return;
    }
    window.open(`/knowledge/${form.slug}`, "_blank", "noopener");
  };

  if (loading) return <AdminLayout><p className="text-sm text-ink-500">جارٍ التحميل…</p></AdminLayout>;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <button
              type="button"
              onClick={() => navigate("/admin/knowledge")}
              className="inline-flex items-center gap-1.5 text-[12px] font-medium text-ink-500 hover:text-intel-900"
            >
              <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
              العودة إلى المكتبة
            </button>
            <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-surface-200 bg-surface-50 px-3 py-1 text-[10.5px] font-semibold uppercase tracking-widest text-intel-800">
              <Library className="h-3 w-3 text-intel-700" strokeWidth={2} aria-hidden />
              {isNew ? "قصّة جديدة" : "تحرير القصّة"}
            </div>
            <h1 className="mt-2 text-2xl font-semibold tracking-tight text-ink-900">
              {isNew ? "أضِف قصّة جديدة" : form.title || "قصّة بدون عنوان"}
            </h1>
            <p className="mt-1 text-[12px] text-ink-500">
              العدد الحالي للكلمات:{" "}
              <span dir="ltr" className={`num font-mono tabular-nums font-semibold ${wordCount > 200 ? "text-risk-700" : "text-ink-900"}`}>
                {wordCount}
              </span> / 200
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={preview}
              data-testid="knowledge-editor-preview"
              className="inline-flex h-10 items-center gap-1.5 rounded-lg border border-surface-200 bg-white px-4 text-sm font-medium text-ink-700 hover:border-intel-300 hover:text-intel-900"
            >
              <Eye className="h-4 w-4" strokeWidth={1.75} aria-hidden />
              معاينة
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => save()}
              data-testid="knowledge-editor-save"
              className="inline-flex h-10 items-center gap-1.5 rounded-lg border border-surface-200 bg-white px-4 text-sm font-medium text-ink-900 hover:border-intel-300"
            >
              <Save className="h-4 w-4" strokeWidth={1.75} aria-hidden />
              حفظ
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => save("published")}
              data-testid="knowledge-editor-publish"
              className="inline-flex h-10 items-center gap-1.5 rounded-lg bg-fursa-700 px-4 text-sm font-medium text-white hover:bg-fursa-800"
            >
              نشر
            </button>
          </div>
        </header>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Left: main fields */}
          <div className="space-y-5 lg:col-span-2">
            <Card>
              <Field label="Slug (المعرف الرابط)" hint="مثال: jeff-bezos-day-one" required>
                <input
                  value={form.slug}
                  onChange={(e) => update({ slug: e.target.value.replace(/\s+/g, "-").toLowerCase() })}
                  data-testid="knowledge-field-slug"
                  className="w-full rounded-md border border-surface-200 bg-white px-3 py-2 text-sm text-ink-900 outline-none focus:border-intel-500"
                  dir="ltr"
                />
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="التصنيف" required>
                  <select
                    value={form.category}
                    onChange={(e) => update({ category: e.target.value })}
                    data-testid="knowledge-field-category"
                    className="w-full rounded-md border border-surface-200 bg-white px-3 py-2 text-sm text-ink-900"
                  >
                    {KNOWLEDGE_CATEGORIES.map((c) => (
                      <option key={c.key} value={c.key} disabled={!c.available}>
                        {c.label}{c.available ? "" : " (قريباً)"}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="الاسم (شخصية أو شركة)" required>
                  <input
                    value={form.subjectName}
                    onChange={(e) => update({ subjectName: e.target.value })}
                    data-testid="knowledge-field-subject"
                    className="w-full rounded-md border border-surface-200 bg-white px-3 py-2 text-sm text-ink-900"
                  />
                </Field>
              </div>
              <Field label="العنوان (المبدأ العملي)" required>
                <input
                  value={form.title}
                  onChange={(e) => update({ title: e.target.value })}
                  data-testid="knowledge-field-title"
                  className="w-full rounded-md border border-surface-200 bg-white px-3 py-2 text-sm text-ink-900"
                />
              </Field>
              <Field label="مقدّمة قصيرة" hint="جملة أو جملتان تُلخّصان القصّة" required>
                <textarea
                  value={form.intro}
                  onChange={(e) => update({ intro: e.target.value })}
                  data-testid="knowledge-field-intro"
                  rows={3}
                  className="w-full resize-none rounded-md border border-surface-200 bg-white px-3 py-2 text-sm text-ink-900 arabic-body"
                />
              </Field>
            </Card>

            <Card>
              <Field label="ما الذي حدث؟" required>
                <textarea
                  value={form.whatHappened}
                  onChange={(e) => update({ whatHappened: e.target.value })}
                  data-testid="knowledge-field-what"
                  rows={4}
                  className="w-full rounded-md border border-surface-200 bg-white px-3 py-2 text-sm text-ink-900 arabic-body"
                />
              </Field>
              <Field label="لماذا نجح؟" required>
                <textarea
                  value={form.whySucceeded}
                  onChange={(e) => update({ whySucceeded: e.target.value })}
                  data-testid="knowledge-field-why"
                  rows={4}
                  className="w-full rounded-md border border-surface-200 bg-white px-3 py-2 text-sm text-ink-900 arabic-body"
                />
              </Field>
              <Field label="ماذا نتعلّم؟" hint="مبدأ عملي واحد فقط" required>
                <textarea
                  value={form.lessonsLearned}
                  onChange={(e) => update({ lessonsLearned: e.target.value })}
                  data-testid="knowledge-field-lesson"
                  rows={4}
                  className="w-full rounded-md border border-surface-200 bg-white px-3 py-2 text-sm text-ink-900 arabic-body"
                />
              </Field>
            </Card>

            {/* Sources */}
            <Card>
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-ink-900">المصادر</h2>
                <button
                  type="button"
                  onClick={addSource}
                  data-testid="knowledge-source-add"
                  className="inline-flex items-center gap-1 rounded-md border border-surface-200 bg-white px-3 py-1.5 text-[12px] font-medium text-ink-700 hover:border-intel-300"
                >
                  <Plus className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
                  إضافة مصدر
                </button>
              </div>
              {(form.sources || []).length === 0 && (
                <p className="mt-3 text-[12px] text-ink-500">لا توجد مصادر بعد.</p>
              )}
              <ul className="mt-3 space-y-3">
                {(form.sources || []).map((s, i) => (
                  <li
                    key={i}
                    data-testid={`knowledge-source-row-${i}`}
                    className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center"
                  >
                    <input
                      placeholder="اسم المصدر"
                      value={s.name || ""}
                      onChange={(e) => updSource(i, { name: e.target.value })}
                      className="rounded-md border border-surface-200 bg-white px-3 py-2 text-sm"
                    />
                    <input
                      placeholder="https://…"
                      dir="ltr"
                      value={s.url || ""}
                      onChange={(e) => updSource(i, { url: e.target.value })}
                      className="rounded-md border border-surface-200 bg-white px-3 py-2 text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => rmSource(i)}
                      aria-label="حذف المصدر"
                      data-testid={`knowledge-source-remove-${i}`}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-md text-ink-500 hover:text-risk-700"
                    >
                      <Trash2 className="h-4 w-4" strokeWidth={1.75} aria-hidden />
                    </button>
                  </li>
                ))}
              </ul>
            </Card>
          </div>

          {/* Right: meta panel */}
          <aside className="space-y-5">
            <Card>
              <Field label="مدّة القراءة" hint="بالدقائق">
                <input
                  type="number"
                  min={1}
                  max={15}
                  value={form.readTimeMinutes}
                  onChange={(e) => update({ readTimeMinutes: Number(e.target.value) || 2 })}
                  data-testid="knowledge-field-readtime"
                  className="w-full rounded-md border border-surface-200 bg-white px-3 py-2 text-sm num font-mono tabular-nums"
                  dir="ltr"
                />
              </Field>
              <Field label="الدولة">
                <input
                  value={form.country}
                  onChange={(e) => update({ country: e.target.value })}
                  className="w-full rounded-md border border-surface-200 bg-white px-3 py-2 text-sm"
                />
              </Field>
              <Field label="القطاع">
                <input
                  value={form.industry}
                  onChange={(e) => update({ industry: e.target.value })}
                  className="w-full rounded-md border border-surface-200 bg-white px-3 py-2 text-sm"
                />
              </Field>
              <Field label="رابط صورة الغلاف" hint="اختياري — رابط مباشر لصورة مستضافة (URL)">
                <input
                  value={form.coverImageUrl}
                  onChange={(e) => update({ coverImageUrl: e.target.value })}
                  dir="ltr"
                  data-testid="knowledge-cover-url"
                  placeholder="https://…"
                  className="w-full rounded-md border border-surface-200 bg-white px-3 py-2 text-sm"
                />
              </Field>
              <Field label="مصدر الصورة" hint="اسم/رابط المصدر — يُعرض تحت الصورة كإسناد">
                <input
                  value={form.coverImageSource}
                  onChange={(e) => update({ coverImageSource: e.target.value })}
                  data-testid="knowledge-cover-source"
                  placeholder="Unsplash · اسم المصور"
                  className="w-full rounded-md border border-surface-200 bg-white px-3 py-2 text-sm"
                />
              </Field>
              <Field label="النص البديل للصورة (Alt)" hint="لأغراض الوصول والقارئات الشاشية">
                <input
                  value={form.coverImageAlt}
                  onChange={(e) => update({ coverImageAlt: e.target.value })}
                  data-testid="knowledge-cover-alt"
                  className="w-full rounded-md border border-surface-200 bg-white px-3 py-2 text-sm"
                />
              </Field>
              <Field label="تاريخ النشر المعروض" hint="مثال: الاثنين، 14 يوليو 2026">
                <input
                  value={form.publishedDisplay}
                  onChange={(e) => update({ publishedDisplay: e.target.value })}
                  className="w-full rounded-md border border-surface-200 bg-white px-3 py-2 text-sm"
                />
              </Field>
            </Card>
            <Card>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.isFeatured}
                  onChange={(e) => update({ isFeatured: e.target.checked })}
                  data-testid="knowledge-field-featured"
                  className="h-4 w-4 rounded border-surface-300 text-fursa-700 focus:ring-fursa-500"
                />
                <span className="text-ink-900">مميّزة (تظهر في المقدّمة)</span>
              </label>
              <Field label="الحالة">
                <select
                  value={form.status}
                  onChange={(e) => update({ status: e.target.value })}
                  data-testid="knowledge-field-status"
                  className="mt-1 w-full rounded-md border border-surface-200 bg-white px-3 py-2 text-sm"
                >
                  <option value="draft">مسوَّدة</option>
                  <option value="published">منشورة</option>
                  <option value="archived">مؤرشفة</option>
                </select>
              </Field>
            </Card>
          </aside>
        </div>
      </div>
    </AdminLayout>
  );
}

/* ─── Primitives ─────────────────────────────────────────────── */

const Card = ({ children }) => (
  <section className="space-y-4 rounded-xl border border-surface-200 bg-white p-5 shadow-card">
    {children}
  </section>
);

const Field = ({ label, hint, required, children }) => (
  <div>
    <div className="flex items-baseline justify-between">
      <label className="text-[11.5px] font-semibold text-ink-900">
        {label}
        {required && <span className="ms-1 text-risk-500">*</span>}
      </label>
      {hint && <span className="text-[11px] text-ink-500">{hint}</span>}
    </div>
    <div className="mt-1.5">{children}</div>
  </div>
);
